import {
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
  Injector
} from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { QuestionnaireService } from "../../services/questionnaire.service";
import { EdlModalEvidence } from "../../modals/edl-evidence-modal.component";
import { CompleteUserFormComponent } from "../../questionnaires/user-form/complete-user-form.component";
import { NavigationSection } from "../../modals/edl-modal-details/navigation-section.model";
import { NavigationItem } from "../../modals/edl-modal-details/navigation-item.model";
import { CplCycle } from "../../model/cpl-cycle.model";
import { CplItem } from "../../model/cpl-item.model";
import { ComplianceItemHelpersService } from "../../services/compliance-item-helpers/compliance-item-helpers.service";
import { FileAttachmentType } from "../compliance-items/compliance-item/enums/file-attachment-type.enum";
import { NoteTypes } from "../compliance-items/compliance-item/enums/note-types.enum";
import { NotesToInclude } from "../compliance-items/compliance-item/enums/notes-to-include.enum";
import { Act48StatusModalComponent } from "../compliance-items/compliance-item/modals/a48-status-modal/a48-status-modal.component";
import { VerifyModalComponent } from "../compliance-items/compliance-item/modals/verify-modal/verify-modal.component";
import { NavigationItemId } from "../../modals/edl-modal-details/navigation-item-id.enum";
import { UserCycleSummary } from "../user-cycle-summary/user-cycle-summary.component";
import { EdllNotesComponent } from "src/app/components/edl-notes/edl-notes.component";
import { PortalSettings, SessionService, User } from "@edl-development/edl-common";
import { AccessRoleHelper } from "src/app/model/helpers/access-role.helper";
import { CplRequirement } from "src/app/model/enum/cpl-requirement.enum";
import { TimeTrackingFormService } from "src/app/time-tracking/services/time-tracking-form/time-tracking-form.service";
import { UntypedFormGroup, UntypedFormBuilder } from "@angular/forms";
import { FlexDetailsComponent } from "./flex-details/flex-details.component";

@Component({
  selector: "user-cycle-details",
  templateUrl: "./user-cycle-details-modal.component.html",
  encapsulation: ViewEncapsulation.None
})
export class UserCycleDetailsModal implements OnInit {
  @Input() originalUserCycle: CplCycle;
  @Input() complianceItem: CplItem;
  @Input() startingNavigationItemId: string = NavigationItemId.NOTES;
  @Input() CanManageItem: boolean;
  @Input() isUser: boolean = false;

  userCycle: CplCycle;
  isReadOnly: boolean;
  navigationSections: Array<NavigationSection>;
  isVerifier:boolean;
  user: User;
  portalSettings: PortalSettings;
  form: UntypedFormGroup;

  constructor(
    public activeModal: NgbActiveModal,
    private questionnaireService: QuestionnaireService,
    private sessionService: SessionService,
    private complianceItemHelpersService: ComplianceItemHelpersService,
    private timeTrackingFormService: TimeTrackingFormService
  ) {}

  get isSupervisorView() {
    return this.complianceItemHelpersService.isSupervisorView(this.isUser);
  }

  ngOnInit() {
    this.userCycle = this.originalUserCycle;
    this.isReadOnly = !this.complianceItem.IsPublished;
    this.user = this.sessionService.user;
    this.isVerifier = this.userCycle.CanVerify;
    this.portalSettings = this.sessionService.PortalSettings;

    this.createFlexDetailsForm();

    this.loadNavigationItems();
  }

  loadNavigationItems() {
    this.navigationSections = Array<NavigationSection>();

    // Summary Section
    this.pushSection('Summary');
    this.pushItem(0, new NavigationItem(NavigationItemId.DETAILS, 'Details', UserCycleSummary, { UserCycle: this.userCycle, ComplianceItem: this.complianceItem }, this.loadSummary));
    if (this.isSupervisorView) {
      this.pushItem(0, new NavigationItem(NavigationItemId.NOTES, 'Notes', EdllNotesComponent, { UserCycle: this.userCycle, ComplianceItem: this.complianceItem, notesToInclude: NotesToInclude.BOTH },this.loadNotes));
    }


    // Act 48 tab: Show for supervisor view OR My Items view when applicable
    if (this.portalSettings.Act48Enabled && this.complianceItem.Act48Detail != null && this.complianceItem.Act48Detail.Applicable) {
      this.pushItem(0, new NavigationItem(NavigationItemId.ACT48, 'Act 48', Act48StatusModalComponent, { CplUserRequest: this.userCycle, CplRequestItem: this.complianceItem },this.loadAct48Info));
    }
    
    if (this.isSupervisorView && this.complianceItem?.FlexDetails?.Applicable) {
      this.pushItem(0, new NavigationItem(
        NavigationItemId.FLEX, 
        'Flex', 
        FlexDetailsComponent, 
        { 
          form: this.form?.get('FlexDetails'),
          userCycle: this.userCycle,
          complianceItem: this.complianceItem,
          isReadOnly: this.isReadOnly
        }, 
        this.loadFlexDetails
      ));
    }

    if (this.isSupervisorView && this.CanManageItem && !this.isReadOnly) {
      this.pushItem(0, new NavigationItem(NavigationItemId.APPROVAL, 'Actions', VerifyModalComponent,{ UserCycle: this.userCycle, ComplianceItem: this.complianceItem }, this.loadApproval));
    }

    // User Cycle Details Seciton
    const showQfm = this.userCycle.QfmID;
    const showEvidence = this.userCycle.HasAttachments || this.complianceItem.EvidenceRequired !== CplRequirement.NONE;

    if (showQfm || showEvidence) {
      this.pushSection('User Cycle Details');
      if (showQfm) {
        this.pushItem(1, new NavigationItem(NavigationItemId.QUESTIONNAIRE, `Questionnaire - ${this.userCycle.QfmDisplayTitle}`, CompleteUserFormComponent, { UserFormId: this.userCycle.QfmID }, this.loadQuestionaireData, this.complianceItem.QfmRequired, { isFollowup: false }));
      }

      if (showEvidence) {
        this.pushItem(1, new NavigationItem(NavigationItemId.EVIDENCE, 'Evidence', EdlModalEvidence,{ UserCycle: this.userCycle, ComplianceItem: this.complianceItem }, this.loadEvidence, this.complianceItem.EvidenceRequired === CplRequirement.REQUIRED));
      }
    }
  }

  pushSection(title: string) {
    this.navigationSections.push(new NavigationSection(title, new Array<NavigationItem>()));
  }

  pushItem(sectionIndex: number, item: NavigationItem) {
    this.navigationSections[sectionIndex].navigationItems.push(item);
  }

  loadSummary = (ref: any, _args: any) => {
    const injector = Injector.create({ providers: [], parent: ref.container.parentInjector });
    const componentRef = ref.container.createComponent(ref.factory,0,injector);
    
    componentRef.instance.userCycle = this.userCycle;
    componentRef.instance.complianceItem= this.complianceItem;
    componentRef.instance.isUser = this.isUser;
    componentRef.instance.onSubmissionComplete.subscribe(() => {
      ref.parent.onSubmissionComplete(false, true)
    });
    ref.parent.busy = false;
  }

  loadNotes = (ref: any, _args: any) => {
    const injector = Injector.create({ providers: [], parent: ref.container.parentInjector });
    const componentRef = ref.container.createComponent(ref.factory, 0, injector);

    componentRef.instance.typeOfNote = NoteTypes.USER_CYCLE;
    componentRef.instance.typeId = ref.data.UserCycle.CycleID;
    componentRef.instance.notesToInclude = ref.data.notesToInclude;
    componentRef.instance.isReadOnly = ref.parent.isReadOnly;
    componentRef.instance.onSubmissionComplete.subscribe(ref.parent.onSubmissionComplete);
    ref.parent.busy = false;
  }


  loadAct48Info = (ref: any, _args: any) => {
    const injector =  Injector.create({ providers: [], parent: ref.container.parentInjector });
    const componentRef = ref.container.createComponent(ref.factory, 0, injector);

    componentRef.instance.userCycle = this.userCycle;
    componentRef.instance.cplItem = this.complianceItem;
    componentRef.instance.canClose = false;
    componentRef.instance.onSubmissionComplete
      .subscribe(({ success, ShouldSubmitToPerms } : { success: boolean, ShouldSubmitToPerms: boolean }) => {
        if (success) {
          this.userCycle.ShouldSubmitToPerms = ShouldSubmitToPerms;
        }

        ref.parent.onSubmissionComplete(success);
      });

    ref.parent.busy = false;
  }

  loadFlexDetails = (ref: any, _args: any) => {
    const injector = Injector.create({ providers: [], parent: ref.container.parentInjector });
    const componentRef = ref.container.createComponent(ref.factory, 0, injector);

    if (!this.form) {
      this.createFlexDetailsForm();
    }

    componentRef.instance.form = this.form?.get('FlexDetails');
    componentRef.instance.userCycle = this.userCycle;
    componentRef.instance.complianceItem = this.complianceItem;
    componentRef.instance.isReadOnly = this.isReadOnly;

    componentRef.instance.onSaveComplete
      .subscribe(({ success }: { success: boolean }) => {
        if (success) {
          //ensure refresh
          this.createFlexDetailsForm();
        }
        
        ref.parent.onSubmissionComplete(success);
      });

    ref.parent.busy = false;
  }

  loadApproval = (ref: any, _args: any) => {
    const injector =  Injector.create({ providers: [], parent: ref.container.parentInjector });
    const componentRef = ref.container.createComponent(ref.factory, 0,injector);

    componentRef.instance.complianceItem = this.complianceItem;
    componentRef.instance.userCycle = this.userCycle;
    componentRef.instance.canClose = false;
    componentRef.instance.onSubmissionComplete.subscribe((success: boolean) => ref.parent.onSubmissionComplete(success, success));

    ref.parent.busy = false;
  }

  loadEvidence = (ref: any, _args: any) => {
    const injector =  Injector.create({
      providers: [],
      parent: ref.container.parentInjector
    });
    const componentRef = ref.container.createComponent(
      ref.factory,
      0,
      injector
    );

    const UserCycel = this.userCycle;
    const UserCycleComplete: Boolean = this.complianceItemHelpersService.isFinalUserStatus(UserCycel.UserStatus);

    componentRef.instance.fileType = FileAttachmentType.COMPLIANCECYCLE;
    componentRef.instance.fileTypeId = ref.data.UserCycle.CycleID;
    componentRef.instance.isReadOnly = UserCycleComplete || ref.parent.isReadOnly;
    componentRef.instance.isDeleteDisabled = this.userCycle.UserID === this.sessionService.user.UserID;
    componentRef.instance.isUploadDisabled = this.complianceItem.EvidenceRequired === CplRequirement.NONE;
    componentRef.instance.canClose = false;
    componentRef.instance.onSubmissionComplete.subscribe(({ success, files }) => {
      this.userCycle.HasAttachments = files.length > 0;
      ref.parent.onSubmissionComplete(success);
    });

    ref.parent.busy = false;
  }

  loadQuestionaireData = (ref: any, _args: any) => {

    this.questionnaireService
      .getUserForm(ref.data.UserFormId)
      .subscribe((response: any) => {
        const injector =  Injector.create({
          providers: [],
          parent: ref.container.parentInjector
        });
        const componentRef = ref.container.createComponent(
          ref.factory,
          0,
          injector
        );
        
        const UserCycel = this.userCycle;
        const UserCycleComplete: Boolean = this.complianceItemHelpersService.isFinalUserStatus(UserCycel.UserStatus);

        componentRef.instance.userFormDetails = response;
        componentRef.instance.masterComplete = UserCycleComplete || ref.parent.isReadOnly;
        componentRef.instance.canClose = false;
        componentRef.instance.onSubmissionComplete.subscribe(({ success, isQuestionnaireComplete }) => {
          if (isQuestionnaireComplete) this.userCycle.HasCompletedQuestionnaire = isQuestionnaireComplete;
          ref.parent.onSubmissionComplete(success)
        });

        ref.parent.busy = false;
      });
  }

  private createFlexDetailsForm() {
    // Use userCycle.FlexHours if available, otherwise fallback to complianceItem.FlexDetails
    let flexDetails = { ...this.complianceItem.FlexDetails };
    if (this.userCycle && typeof this.userCycle.FlexHours === 'number') {
      flexDetails = { ...flexDetails, Hours: this.userCycle.FlexHours };
    }
    const { timeTrackingForm, timeTrackingSubscription } = this.timeTrackingFormService.buildForm(
      flexDetails,
      true // isComplianceItem
    );
    this.form = new UntypedFormGroup({
      FlexDetails: timeTrackingForm
    });
  }
}
